import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, LayoutGrid, Save, Search } from "lucide-react";
import { toast } from "sonner";
import { useEventMeta } from "@/lib/event-meta";
import { API_BASE } from "@/lib/utils";
import { bulkUpdateAttendees, fetchTicketTypes } from "@/lib/attendeesApi";

const PAGE_SIZE = 100;

async function fetchAttendeePage(eventId, { offset, q, ticket, segment = "invitees" }) {
  const params = new URLSearchParams({
    limit: String(PAGE_SIZE),
    offset: String(offset),
    sort: "ticket",
    sort_dir: "asc",
    segment,
  });
  if (q?.trim()) params.set("q", q.trim());
  if (ticket && ticket !== "all") params.set("ticket", ticket);
  const res = await fetch(`${API_BASE}/api/events/${eventId}/attendees?${params}`);
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json();
  const rows = Array.isArray(data.attendees) ? data.attendees : [];
  return { rows, total: Number(data.total) || rows.length };
}

export default function SeatArrangement() {
  const { id } = useParams();
  const event = useEventMeta(id);
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [ticketFilter, setTicketFilter] = useState("all");
  const [ticketTypeOptions, setTicketTypeOptions] = useState([]);
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { rows: batch, total: t } = await fetchAttendeePage(id, {
        offset: page * PAGE_SIZE,
        q,
        ticket: ticketFilter,
      });
      setRows(batch);
      setTotal(t);
      setDrafts((prev) => {
        const next = { ...prev };
        for (const row of batch) {
          if (row.id && next[row.id] === undefined) {
            next[row.id] = row.seat || "";
          }
        }
        return next;
      });
    } catch (err) {
      toast.error(err?.message || "Could not load attendees");
    } finally {
      setLoading(false);
    }
  }, [id, page, q, ticketFilter]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const tickets = await fetchTicketTypes(id);
        if (!cancel) setTicketTypeOptions(tickets);
      } catch {
        if (!cancel) setTicketTypeOptions([]);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const visibleRows = useMemo(() => {
    if (!unassignedOnly) return rows;
    return rows.filter((r) => !(drafts[r.id] || r.seat || "").trim());
  }, [rows, drafts, unassignedOnly]);

  const dirtyIds = useMemo(() => {
    return rows
      .filter((r) => r.id && String(drafts[r.id] ?? r.seat ?? "").trim() !== String(r.seat ?? "").trim())
      .map((r) => r.id);
  }, [rows, drafts]);

  const assignedOnPage = useMemo(
    () => visibleRows.filter((r) => (drafts[r.id] ?? r.seat ?? "").trim()).length,
    [visibleRows, drafts],
  );

  const saveSeats = async () => {
    if (!dirtyIds.length) {
      toast.message("No seat changes to save");
      return;
    }
    setSaving(true);
    try {
      const updates = dirtyIds.map((aid) => ({
        id: aid,
        patch: { seat: String(drafts[aid] ?? "").trim() },
      }));
      const result = await bulkUpdateAttendees(id, updates);
      toast.success(`Saved ${result.updated ?? updates.length} seat assignment(s)`);
      await load();
    } catch (err) {
      toast.error(err?.message || "Could not save seats");
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="h-full min-h-0 overflow-y-auto" style={{ background: "var(--bg-base)" }} data-testid="seat-arrangement">
      <div className="border-b border-[var(--border-default)] px-4 md:px-8 py-6">
        <Link to={`/admin/events/${id}/attendees`} className="btn-ghost text-xs mb-4 inline-flex">
          <ArrowLeft size={14} /> Back to attendees
        </Link>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-overline flex items-center gap-2">
              <LayoutGrid size={12} /> Seat arrangement
            </div>
            <h1 className="font-heading text-2xl md:text-3xl tracking-tight font-medium mt-2">{event.title}</h1>
            <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-2xl">
              Assign seats for check-in. Ushers will see the seat when an attendee is scanned at the gate.
            </p>
          </div>
          <button type="button" className="btn-primary" disabled={saving || !dirtyIds.length} onClick={saveSeats}>
            <Save size={14} /> {saving ? "Saving…" : `Save changes (${dirtyIds.length})`}
          </button>
        </div>
      </div>

      <div className="px-4 md:px-8 py-4 flex flex-wrap items-center gap-3 border-b border-[var(--border-default)]">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] pointer-events-none" aria-hidden />
          <input
            className="input-dark input-dark--with-icon w-full"
            placeholder="Search name, company, seat…"
            value={q}
            onChange={(ev) => {
              setPage(0);
              setQ(ev.target.value);
            }}
          />
        </div>
        <select
          className="input-dark max-w-[200px]"
          value={ticketFilter}
          onChange={(ev) => {
            setPage(0);
            setTicketFilter(ev.target.value);
          }}
          aria-label="Filter by ticket type"
        >
          <option value="all">All tickets</option>
          {ticketTypeOptions.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)] cursor-pointer">
          <input
            type="checkbox"
            checked={unassignedOnly}
            onChange={(ev) => setUnassignedOnly(ev.target.checked)}
          />
          Unassigned only
        </label>
        <div className="text-xs font-mono text-[var(--text-tertiary)] ml-auto">
          {assignedOnPage} assigned on page · {total.toLocaleString()} total
        </div>
      </div>

      <div className="px-4 md:px-8 py-6">
        <div className="border border-[var(--border-default)] overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border-default)] bg-[var(--bg-surface)] text-left">
                <th className="px-4 py-3 text-overline font-normal">Name</th>
                <th className="px-4 py-3 text-overline font-normal">Ticket</th>
                <th className="px-4 py-3 text-overline font-normal">Company</th>
                <th className="px-4 py-3 text-overline font-normal">Status</th>
                <th className="px-4 py-3 text-overline font-normal w-[180px]">Seat</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-[var(--text-tertiary)]">
                    Loading attendees…
                  </td>
                </tr>
              ) : visibleRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-[var(--text-tertiary)]">
                    No attendees match this filter.
                  </td>
                </tr>
              ) : (
                visibleRows.map((row) => (
                  <tr key={row.id} className="border-b border-[var(--border-default)] hover:bg-[var(--bg-surface)]">
                    <td className="px-4 py-3 font-medium">{row.name}</td>
                    <td className="px-4 py-3 font-mono text-xs">{row.ticket || "—"}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">{row.company || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs">{row.status || "—"}</td>
                    <td className="px-4 py-3">
                      <input
                        className="input-dark w-full font-mono text-xs"
                        placeholder="e.g. A-12"
                        value={drafts[row.id] ?? row.seat ?? ""}
                        onChange={(ev) =>
                          setDrafts((prev) => ({
                            ...prev,
                            [row.id]: ev.target.value,
                          }))
                        }
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex items-center justify-between text-sm font-mono text-[var(--text-tertiary)]">
          <button type="button" className="btn-ghost text-xs" disabled={page <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
            Previous
          </button>
          <span>
            Page {page + 1} / {totalPages}
          </span>
          <button
            type="button"
            className="btn-ghost text-xs"
            disabled={page + 1 >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
